import { useMemo } from 'react';

import styles from './Progressbar.module.scss';
import { useLocalization } from '../../../../../../../../../lib/hooks/useLocalization';
import { AppText } from '../../../../../../../../../ui/text/AppText';
import { hex2rgba } from '../../../../../utils/helpers';

const ALL_DISTANCE = 100;
const TEXT_OPACITY = 0.7;
const BACKGROUND_OPACITY = 0.1;
const BORDER_OPACITY = 0.4;

export function getChaptersProgressCaption(completed, total, i18n = {}) {
  const safeTotal = Math.max(0, total || 0);
  if (!safeTotal) return '';

  const safeCompleted = Math.min(Math.max(0, completed || 0), safeTotal);

  const defaults = {
    missingTemplatePlural: 'Es fehlen noch {{count}} {{part}} der Lösung',
    missingTemplateSingular: 'Es fehlt noch {{count}} {{part}} der Lösung',

    partSingular: 'Teil',
    partPlural: 'Teile',

    fullyConfigured: 'Die Lösung ist vollständig konfiguriert',
  };

  // eslint-disable-next-line id-length
  const t = { ...defaults, ...i18n };

  if (safeCompleted >= safeTotal) {
    return `${safeCompleted} / ${safeTotal}: ${t.fullyConfigured}`;
  }

  const missing = safeTotal - safeCompleted;

  const isSingular = missing === 1;
  const part = isSingular ? t.partSingular : t.partPlural;
  const template = isSingular ? t.missingTemplateSingular : t.missingTemplatePlural;

  const missingText = template.replace('{{count}}', String(missing)).replace('{{part}}', part);

  return `${safeCompleted} / ${safeTotal}: ${missingText}`;
}

export function Progressbar({ footer, colors, pagesFlow, showChapterIds, fonts }) {
  const { title: progressbarTitle } = useLocalization().translations.unique.components.productBasedFinder.progressbar;

  const { allChapters } = pagesFlow || {};
  const { progressbarBackgroundColor } = footer;

  const progressBarColor = colors[progressbarBackgroundColor] || '#005CA8';

  const visibleChapters = useMemo(() => {
    if (Array.isArray(showChapterIds) && showChapterIds.length > 0) {
      return allChapters?.filter(ch => showChapterIds.includes(ch.id));
    }
    return allChapters;
  }, [allChapters, showChapterIds]);

  const numberOfChapters = visibleChapters?.length || 0;

  const completedChapters = useMemo(
    () =>
      visibleChapters?.reduce((acc, ch) => {
        const count = Array.isArray(ch?.resultSelectedProducts) ? ch.resultSelectedProducts.length : 0;
        return acc + (count > 0 ? 1 : 0);
      }, 0) || 0,
    [visibleChapters]
  );

  const rawProgress = numberOfChapters > 0 ? (completedChapters / numberOfChapters) * ALL_DISTANCE : 0;
  const progressPercentage = Math.max(0, Math.min(ALL_DISTANCE, rawProgress));

  const chapterTicks = useMemo(() => {
    if (numberOfChapters <= 1) return [];
    return Array.from({ length: numberOfChapters - 1 }, (__, i) => ((i + 1) / numberOfChapters) * ALL_DISTANCE);
  }, [numberOfChapters]);

  const captionText = useMemo(() => getChaptersProgressCaption(completedChapters, numberOfChapters), [completedChapters, numberOfChapters]);

  return (
    <div
      className={styles.wrapper}
      style={{
        backgroundColor: hex2rgba(progressBarColor, BACKGROUND_OPACITY),
        borderColor: hex2rgba(progressBarColor, BORDER_OPACITY),
      }}
    >
      <div className={styles.header}>
        <AppText textValue={progressbarTitle} className={styles.title} style={{ color: progressBarColor, fontFamily: fonts.body.font }} />
        <div className={styles.percent} style={{ color: progressBarColor, fontFamily: fonts.body.font }}>
          {Math.floor(progressPercentage)}%
        </div>
      </div>

      <div className={styles.track} role='progressbar' aria-valuenow={Math.round(progressPercentage)} aria-valuemin={0} aria-valuemax={100}>
        <div className={styles.barContainer}>
          <div
            className={styles.barFill}
            style={{
              width: `${progressPercentage}%`,
              backgroundColor: progressBarColor,
            }}
          />
        </div>

        {chapterTicks.map(tick => (
          <span key={tick} className={styles.tick} style={{ left: `${tick}%` }} />
        ))}
      </div>

      <div
        className={styles.caption}
        style={{
          color: hex2rgba(progressBarColor, TEXT_OPACITY),
          fontFamily: fonts.body.font,
        }}
      >
        {captionText}
      </div>
    </div>
  );
}
